import { Link } from "react-router-dom";
import { useState } from "react";
import { useTranslation } from "react-i18next";
import { useLocalePath } from "../hooks/useLocalePath";
import { SEOHead } from "../components/SEOHead";
import { Button } from "../components/ui/button";
import { Badge } from "../components/ui/badge";
import { Card, CardContent } from "../components/ui/card";
import {
  ChevronRight, ChevronDown, ShieldCheck, HelpCircle, Compass,
  AlertTriangle, Copy, Camera, Users, FileWarning, KeyRound,
  ClipboardList,
} from "lucide-react";
import { SiteFooter } from "../components/SiteFooter";
import { DVGuidesNav } from "../components/DVGuidesNav";
import { useAnalytics, EVENTS } from "../hooks/useAnalytics";

const mistakeIcons = [Copy, Camera, Users, FileWarning, KeyRound];
const mistakeColors = [
  "bg-red-50 border-red-200",
  "bg-amber-50 border-amber-200",
  "bg-orange-50 border-orange-200",
  "bg-rose-50 border-rose-200",
  "bg-violet-50 border-violet-200",
];
const iconColors = [
  "text-red-600",
  "text-amber-600",
  "text-orange-600",
  "text-rose-600",
  "text-violet-600",
];

export default function DVCommonMistakesPage() {
  const { t } = useTranslation();
  const lp = useLocalePath();
  const { trackEvent } = useAnalytics();
  const [openFaq, setOpenFaq] = useState(null);

  const sections = t("dvm.sections", { returnObjects: true });
  const faqs = t("dvm.faqs", { returnObjects: true });

  return (
    <div className="min-h-screen bg-background" data-testid="dv-common-mistakes-page">
      <SEOHead
        title={t("dvm.heroTitle")}
        description={t("dvm.heroSubtitle")}
      />

      {/* HERO */}
      <section className="hero-gradient py-14 md:py-24" data-testid="dvm-hero">
        <div className="max-w-4xl mx-auto px-4 md:px-6 text-center">
          <Badge variant="secondary" className="bg-red-100 text-red-800 hover:bg-red-100 mb-5 font-medium">
            <AlertTriangle className="h-3.5 w-3.5 mr-1.5" />
            {t("dvm.badge")}
          </Badge>
          <h1 className="text-4xl sm:text-5xl lg:text-6xl font-bold tracking-tight leading-tight text-slate-900 mb-5">
            {t("dvm.heroTitle")}
          </h1>
          <p className="text-base md:text-lg leading-relaxed text-slate-600 mb-8 max-w-2xl mx-auto">
            {t("dvm.heroSubtitle")}
          </p>
          <div className="flex flex-col sm:flex-row gap-3 justify-center">
            <Link to={lp("/green-card-lottery#eligibility")} data-testid="dvm-hero-elig">
              <Button size="lg" className="bg-teal-700 hover:bg-teal-800 text-white font-medium shadow-md hover:shadow-lg transition-all duration-300 hover:-translate-y-0.5 w-full sm:w-auto px-8 py-6 text-base"
                onClick={() => trackEvent(EVENTS.CHECK_ELIGIBILITY, { source: "common_mistakes_hero" })}>
                <ShieldCheck className="mr-2 h-4 w-4" /> {t("dvm.ctaEligibility")}
              </Button>
            </Link>
            <Link to={lp("/how-to-fill-dv-lottery-form")} data-testid="dvm-hero-form">
              <Button size="lg" variant="outline" className="border-slate-200 hover:bg-slate-50 font-medium w-full sm:w-auto px-8 py-6 text-base">
                <ClipboardList className="mr-2 h-4 w-4" /> {t("dvm.ctaFormGuide")}
              </Button>
            </Link>
          </div>
        </div>
      </section>

      {/* SECTIONS 1-5: MISTAKE SECTIONS */}
      {Array.isArray(sections) && sections.map((section, i) => {
        const Icon = mistakeIcons[i] || AlertTriangle;
        const isAlt = i % 2 === 1;
        return (
          <section
            key={i}
            className={`py-14 md:py-20 ${isAlt ? "bg-slate-50/60" : "bg-white"}`}
            data-testid={`dvm-section-${i}`}
          >
            <div className="max-w-4xl mx-auto px-4 md:px-6">
              <div className="text-center mb-10">
                <p className="text-xs uppercase tracking-wider font-semibold text-teal-600 mb-2">
                  {t("dvm.mistakeLabel")} {i + 1}
                </p>
                <h2 className="text-2xl md:text-3xl font-semibold tracking-tight text-slate-900">
                  {section.title}
                </h2>
              </div>
              <div className="max-w-3xl mx-auto">
                <Card className={`border shadow-sm ${mistakeColors[i] || mistakeColors[0]}`}>
                  <CardContent className="p-6">
                    <div className="flex items-start gap-4">
                      <div className={`h-10 w-10 rounded-xl bg-white flex items-center justify-center flex-shrink-0 shadow-sm`}>
                        <Icon className={`h-5 w-5 ${iconColors[i] || iconColors[0]}`} />
                      </div>
                      <div>
                        <h3 className="text-base font-semibold text-slate-900 mb-2">{section.subtitle}</h3>
                        <p className="text-sm leading-relaxed text-slate-600 mb-3">{section.desc}</p>
                        <div className="bg-white/60 rounded-lg p-4 border border-slate-200/60">
                          <p className="text-sm leading-relaxed text-slate-700">
                            <span className="font-semibold text-slate-900">{t("dvm.tipLabel")}</span>{" "}
                            {section.tip}
                          </p>
                        </div>
                      </div>
                    </div>
                  </CardContent>
                </Card>
              </div>
              {i === 3 && (
                <div className="mt-8 text-center">
                  <Link to={lp("/auth?mode=register&redirect=%2Fchecklists")} data-testid="dvm-checklist-cta">
                    <Button className="bg-teal-700 hover:bg-teal-800 text-white font-medium"
                      onClick={() => trackEvent(EVENTS.GENERATE_CHECKLIST, { source: "common_mistakes_section" })}>
                      <ClipboardList className="mr-2 h-4 w-4" /> {t("dvm.ctaChecklist")}
                    </Button>
                  </Link>
                </div>
              )}
            </div>
          </section>
        );
      })}

      <DVGuidesNav currentPath="/dv-lottery-common-mistakes" />

      {/* SECTION 6 — FAQ */}
      <section className="py-14 md:py-20 bg-white" data-testid="dvm-faq">
        <div className="max-w-3xl mx-auto px-4 md:px-6">
          <div className="text-center mb-10">
            <p className="text-xs uppercase tracking-wider font-semibold text-teal-600 mb-2">{t("dvm.faqLabel")}</p>
            <h2 className="text-2xl md:text-3xl font-semibold tracking-tight text-slate-900">{t("dvm.faqTitle")}</h2>
          </div>
          <div className="space-y-3">
            {Array.isArray(faqs) && faqs.map((faq, i) => {
              const isOpen = openFaq === i;
              return (
                <Card key={i} className="border-slate-100 shadow-sm overflow-hidden" data-testid={`dvm-faq-${i}`}>
                  <button
                    className="w-full flex items-start gap-3 p-5 text-left hover:bg-slate-50/50 transition-colors"
                    onClick={() => setOpenFaq(isOpen ? null : i)}
                    data-testid={`dvm-faq-toggle-${i}`}
                  >
                    <HelpCircle className="h-5 w-5 text-teal-600 flex-shrink-0 mt-0.5" />
                    <span className="text-sm font-semibold text-slate-900 flex-1">{faq.q}</span>
                    <ChevronDown className={`h-4 w-4 text-slate-400 flex-shrink-0 mt-0.5 transition-transform duration-200 ${isOpen ? "rotate-180" : ""}`} />
                  </button>
                  {isOpen && (
                    <div className="px-5 pb-5">
                      <p className="text-sm leading-relaxed text-slate-600 ml-8">{faq.a}</p>
                    </div>
                  )}
                </Card>
              );
            })}
          </div>
        </div>
      </section>

      {/* FINAL CTA */}
      <section className="py-14 md:py-24 bg-teal-700" data-testid="dvm-final-cta">
        <div className="max-w-3xl mx-auto px-4 md:px-6 text-center">
          <Compass className="h-10 w-10 text-teal-200 mx-auto mb-5" />
          <h2 className="text-2xl md:text-3xl font-semibold tracking-tight text-white mb-3">{t("dvm.finalTitle")}</h2>
          <p className="text-base text-teal-100 mb-8 max-w-xl mx-auto">{t("dvm.finalSubtitle")}</p>
          <div className="flex flex-col sm:flex-row gap-3 justify-center">
            <Link to={lp("/green-card-lottery#eligibility")} data-testid="dvm-final-elig">
              <Button size="lg" className="bg-white text-teal-700 hover:bg-teal-50 font-medium shadow-lg w-full sm:w-auto px-6 py-6 text-base">
                <ShieldCheck className="mr-2 h-4 w-4" /> {t("dvm.ctaEligibility")}
              </Button>
            </Link>
            <Link to={lp("/auth?mode=register")} data-testid="dvm-final-start">
              <Button size="lg" variant="outline" className="border-teal-400 text-white hover:bg-teal-600 font-medium w-full sm:w-auto px-6 py-6 text-base">
                {t("dvm.ctaStartGuide")} <ChevronRight className="ml-2 h-4 w-4" />
              </Button>
            </Link>
          </div>
        </div>
      </section>

      <SiteFooter />
    </div>
  );
}
